import { skipToken, useQuery } from '@tanstack/react-query';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { getVariantUrls } from '@/services/asset.service';
import { useCurrentPage } from '@/hooks/useCurrentPage';
import { QueryKey } from '@/lib/queryKeys';

// Stable reference for the "no data yet" case. Returning a fresh `{}` each render
// would defeat downstream memoization (e.g. the searchAssets memo in home.tsx),
// churning the gallery's items and thrashing selection.
const EMPTY_VARIANT_URLS: Record<string, string> = {};

export const useVariantUrls = (assetIds: Array<string>, variantSlug = 'thumbnail-internal') => {
  const { isPublicSharePage } = useCurrentPage();
  const variantUrlsQueryFn = useAuthenticatedQueryFn(getVariantUrls);

  const sortedIds = [...assetIds].sort();
  const enabled = !isPublicSharePage && sortedIds.length > 0;

  const { data } = useQuery({
    queryKey: [QueryKey.VariantUrls, variantSlug, sortedIds],
    queryFn: enabled ? () => variantUrlsQueryFn({ assetIds: sortedIds, variantSlug }) : skipToken,
    staleTime: 1000 * 60 * 5,
    // Refreshing thumbnail URLs is best-effort: a failure degrades to the stale
    // indexed URLs, so it must not surface a global error toast.
    meta: { suppressGlobalError: true },
  });

  return { variantUrls: data ?? EMPTY_VARIANT_URLS };
};
